import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { errMsg } from "@/lib/api";
import { inp } from "@/components/admin/AdminUi";
import { cassa, money } from "./cassaApi";

const iso = (d) => d.toISOString().slice(0, 10);
const PRESETS = [["Mese", () => { const d = new Date(); return [iso(new Date(d.getFullYear(), d.getMonth(), 1, 12)), iso(d)]; }],
  ["Anno", () => { const d = new Date(); return [`${d.getFullYear()}-01-01`, iso(d)]; }],
  ["12 mesi", () => { const d = new Date(); const s = new Date(d); s.setFullYear(d.getFullYear() - 1); return [iso(s), iso(d)]; }]];
const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function Kpi({ label, v, prev, fmt = money, id }) {
  const delta = prev ? ((v - prev) / Math.abs(prev)) * 100 : null;
  return (
    <div className="rounded-xl border bg-white p-4" data-testid={id}>
      <div className="text-xs text-slate-500">{label}</div><div className="text-2xl font-extrabold text-brand-navy">{fmt(v)}</div>
      {delta !== null && <div className={`text-xs font-semibold ${delta >= 0 ? "text-green-700" : "text-rose-600"}`}>{delta >= 0 ? "+" : ""}{delta.toFixed(0)}% vs periodo prec.</div>}
    </div>
  );
}

function Rank({ title, rows, cols, id }) {
  return (
    <div className="rounded-xl border bg-white overflow-x-auto" data-testid={id}>
      <div className="px-4 py-2 font-extrabold uppercase text-sm border-b">{title}</div>
      <table className="w-full text-sm min-w-[520px]"><thead className="text-xs text-slate-500 bg-slate-50"><tr>{cols.map(([, l]) => <th key={l} className="p-2 text-left">{l}</th>)}</tr></thead>
        <tbody className="divide-y">{rows.slice(0, 20).map((r, i) => <tr key={i}>{cols.map(([f, l]) => <td key={l} className="p-2">{f(r)}</td>)}</tr>)}</tbody></table>
      {!rows.length && <p className="p-4 text-sm text-slate-500">Nessun dato.</p>}
    </div>
  );
}

export function StatsTab() {
  const [range, setRange] = useState(PRESETS[1][1]());
  const [d, setD] = useState(null);
  useEffect(() => { cassa.get("/stats", { params: { start: range[0], end: range[1] } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [range]);
  if (!d) return null;
  const k = d.kpi, p = d.prev;
  const pct = (r) => (r.net ? `${(((r.net - r.cost) / r.net) * 100).toFixed(0)}%` : "—");
  return (
    <div className="space-y-5" data-testid="cassa-stats-tab">
      <div className="flex flex-wrap gap-2 items-center">
        {PRESETS.map(([l, f]) => <button key={l} data-testid={`cassa-stats-${l}`} onClick={() => setRange(f())} className="press h-9 px-3 rounded-full border bg-white text-sm font-semibold">{l}</button>)}
        <input data-testid="cassa-stats-start" type="date" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} className={inp.replace("w-full", "w-40")} />
        <input data-testid="cassa-stats-end" type="date" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} className={inp.replace("w-full", "w-40")} />
      </div>
      {d.zero_cost?.length > 0 && (
        <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-4 text-sm space-y-1" data-testid="cassa-stats-zero-cost">
          <b className="text-amber-900">Attenzione: {d.zero_cost.length} articoli venduti hanno costo di acquisto 0 €</b>, quindi costo merce e margine risultano sovrastimati. Correggili in Articoli → "Solo articoli con costo di acquisto 0" → seleziona → "Acquisto: imposta €".
          <div className="text-xs font-mono text-amber-900 line-clamp-3">{d.zero_cost.map((x) => x.code).join(" · ")}</div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi id="cassa-kpi-gross" label="Incassato (IVA incl.)" v={k.gross} prev={p.gross} />
        <Kpi id="cassa-kpi-net" label="Ricavi netti" v={k.net} prev={p.net} />
        <Kpi id="cassa-kpi-cost" label="Costo merce" v={k.cost} prev={p.cost} />
        <Kpi id="cassa-kpi-margin" label={`Margine (${k.margin_pct}%)`} v={k.margin} prev={p.margin} />
        <Kpi id="cassa-kpi-docs" label={`Vendite (${k.invoices} fatt. · ${k.receipts} ric.)`} v={k.docs} prev={p.docs} fmt={(x) => x} />
      </div>
      <div className="rounded-xl border bg-white p-4 h-72" data-testid="cassa-stats-chart">
        <ResponsiveContainer><BarChart data={d.by_month.map((m) => ({ ...m, label: `${MONTHS[+m.month.slice(5) - 1]} ${m.month.slice(2, 4)}` }))}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} />
          <Tooltip formatter={(v) => money(v)} /><Bar dataKey="net" name="Ricavi netti" fill="#0A2E5C" radius={[4, 4, 0, 0]} /><Bar dataKey="margin" name="Margine" fill="#FACC15" radius={[4, 4, 0, 0]} />
        </BarChart></ResponsiveContainer>
      </div>
      <div className="grid 2xl:grid-cols-2 gap-4">
        <Rank id="cassa-stats-clients" title="Clienti" rows={d.by_client} cols={[[(r) => <b>{r.name}</b>, "Cliente"], [(r) => r.docs, "Vendite"], [(r) => money(r.gross), "Speso"], [(r) => money(r.margin), "Margine"], [pct, "%"], [(r) => r.last.split("-").reverse().join("/"), "Ultimo"]]} />
        <Rank id="cassa-stats-products" title="Articoli" rows={d.by_product} cols={[[(r) => <span className="font-mono">{r.code}</span>, "Codice"], [(r) => <span className="line-clamp-1">{r.description}</span>, "Descrizione"], [(r) => +r.qty.toFixed(2), "Q.tà"], [(r) => money(r.net), "Netto"], [(r) => money(r.margin), "Margine"]]} />
      </div>
      <p className="text-xs text-slate-500">Costi presi dal prezzo di acquisto dell'articolo al momento della vendita. Le righe libere senza codice hanno costo zero.</p>
    </div>
  );
}
